import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { scrollToBuy } from "../lib/constants";
type Preview = {
  src: string;
  label: string;
  caption: string;
};
type Props = {
  previews: Preview[];
};
export function PreviewCarousel({
  previews
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  useEffect(() => {
    if (previews.length < 2) return;
    const timer = window.setInterval(() => {
      setDirection(1);
      setActiveIndex(current => (current + 1) % previews.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [previews.length]);
  if (previews.length === 0) return null;
  const goTo = (nextIndex: number) => {
    const normalized = (nextIndex + previews.length) % previews.length;
    setDirection(normalized > activeIndex ? 1 : -1);
    setActiveIndex(normalized);
  };
  const preview = previews[activeIndex];
  return <div data-id="element-185" data-mp="35ky1rv1ugo74">
      <div className="relative overflow-hidden rounded-2xl border border-[#1f4d3a]/15 bg-white shadow-md" data-id="element-186" data-mp="35l7sjkupqzdv">
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-[#1f4d3a] px-3 py-2" data-id="element-187" data-mp="3fl8gxl7fw5ox">
          <span className="truncate text-[12px] font-bold text-[#f4e3b6]" data-id="element-188" data-mp="3kbowpbyqg3eg">
            {preview.label}
          </span>
          <span className="shrink-0 text-[11px] font-semibold text-white/60" data-id="element-189" data-mp="3oebstp2fsahg">
            {activeIndex + 1}/{previews.length}
          </span>
        </div>

        <AnimatePresence initial={false} mode="wait" data-id="element-190" data-mp="3teg9pg79b787">
          <motion.button key={preview.src} type="button" onClick={scrollToBuy} aria-label={`${preview.label} — ver la oferta del Kit RRHH`} initial={{
          opacity: 0,
          x: direction * 32
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: direction * -32
        }} transition={{
          duration: 0.28,
          ease: "easeOut"
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.18} onDragEnd={(_, info) => {
          if (info.offset.x < -55) goTo(activeIndex + 1);
          if (info.offset.x > 55) goTo(activeIndex - 1);
        }} className="block w-full cursor-grab bg-slate-50 active:cursor-grabbing" data-id="element-191" data-mp="27n9aekafc4q">
            <img src={preview.src} alt={`Vista previa: ${preview.label}`} className="h-56 w-full object-cover object-top sm:h-64" loading="lazy" draggable={false} data-id="element-192" data-mp="2ju6i7hxdeith" />
          </motion.button>
        </AnimatePresence>

        <p className="border-t border-slate-100 px-3 py-2.5 text-[12px] leading-relaxed text-slate-600" data-id="element-193" data-mp="2oleq29j1xw4j">
          {preview.caption}
        </p>
      </div>

      <div className="mt-3 flex items-center justify-center gap-3" data-id="element-194" data-mp="32j3c3kouv9b6">
        <button type="button" onClick={() => goTo(activeIndex - 1)} aria-label="Ver vista previa anterior" className="flex h-8 w-8 items-center justify-center rounded-full border border-[#1f4d3a]/15 bg-white text-[#1f4d3a] transition-colors hover:bg-[#e7f0ea] focus:outline-none focus:ring-2 focus:ring-[#c7a24a]" data-id="element-195" data-mp="2ylfe28rv90f2">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-196" data-mp="3d61lll1enae3" />
        </button>

        <div className="flex items-center gap-1.5" role="tablist" aria-label="Vistas previas del kit" data-id="element-197" data-mp="3d8e2rvzc9gq3">
          {previews.map((item, index) => <button key={item.src} type="button" role="tab" onClick={() => goTo(index)} aria-selected={activeIndex === index} aria-label={`Ver vista previa ${index + 1} de ${previews.length}`} className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-[#c7a24a] ${activeIndex === index ? "w-5 bg-[#1f4d3a]" : "w-2 bg-[#1f4d3a]/25 hover:bg-[#1f4d3a]/50"}`} data-id="element-198" data-mp="33agfbapaznyj" data-mpi={index} />)}
        </div>

        <button type="button" onClick={() => goTo(activeIndex + 1)} aria-label="Ver siguiente vista previa" className="flex h-8 w-8 items-center justify-center rounded-full border border-[#1f4d3a]/15 bg-white text-[#1f4d3a] transition-colors hover:bg-[#e7f0ea] focus:outline-none focus:ring-2 focus:ring-[#c7a24a]" data-id="element-199" data-mp="3iui3ur5ipktw">
          <ChevronRightIcon className="h-4 w-4" data-id="element-200" data-mp="2z4o6lxk1adia" />
        </button>
      </div>
    </div>;
}